import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { db } from '../dbClient';
import { useToast } from '../Toast';

export default function CaseRiskSLAConfig({ company, onSLAUpdate }) {
  const C = useTheme();
  const { showToast } = useToast();

  const [slaConfigs, setSlaConfigs] = useState({
    CRITICAL: { internal_sla_days: 3, regulatory_deadline_days: 3, escalation_days: 0 },
    HIGH: { internal_sla_days: 7, regulatory_deadline_days: 7, escalation_days: 2 },
    MEDIUM: { internal_sla_days: 14, regulatory_deadline_days: 14, escalation_days: 5 },
    LOW: { internal_sla_days: 21, regulatory_deadline_days: 21, escalation_days: 10 },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState(false);
  const [resetting, setResetting] = useState(false);

  const DEFAULT_SLA_CONFIGS = {
    CRITICAL: { internal_sla_days: 3, regulatory_deadline_days: 3, escalation_days: 0 },
    HIGH: { internal_sla_days: 7, regulatory_deadline_days: 7, escalation_days: 2 },
    MEDIUM: { internal_sla_days: 14, regulatory_deadline_days: 14, escalation_days: 5 },
    LOW: { internal_sla_days: 21, regulatory_deadline_days: 21, escalation_days: 10 },
  };

  // Load custom SLA configurations
  useEffect(() => {
    loadSLAConfigs();
  }, [company?.id]);

  async function loadSLAConfigs() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('company_sla_thresholds')
        .select('*')
        .eq('company_id', company.id)
        .eq('entity_type', 'case_risk_level');

      if (error) throw error;

      // Convert array to object keyed by risk_level
      const configMap = {
        CRITICAL: { internal_sla_days: 3, regulatory_deadline_days: 3, escalation_days: 0 },
        HIGH: { internal_sla_days: 7, regulatory_deadline_days: 7, escalation_days: 2 },
        MEDIUM: { internal_sla_days: 14, regulatory_deadline_days: 14, escalation_days: 5 },
        LOW: { internal_sla_days: 21, regulatory_deadline_days: 21, escalation_days: 10 },
      };

      if (data && Array.isArray(data)) {
        data.forEach(config => {
          if (config.risk_level && configMap[config.risk_level]) {
            configMap[config.risk_level] = {
              id: config.id,
              internal_sla_days: config.internal_sla_days,
              regulatory_deadline_days: config.regulatory_deadline_days,
              escalation_days: config.escalation_days,
              notes: config.notes,
            };
          }
        });
      }

      setSlaConfigs(configMap);
      setEdited(false);
    } catch (err) {
      console.error('Error loading SLA configs:', err);
      showToast('Error loading SLA configurations', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function saveSLAConfigs() {
    if (!company?.id) return;
    try {
      setSaving(true);

      for (const [riskLevel, config] of Object.entries(slaConfigs)) {
        if (config.id) {
          // Update existing
          const { error } = await db
            .from('company_sla_thresholds')
            .update({
              internal_sla_days: config.internal_sla_days,
              regulatory_deadline_days: config.regulatory_deadline_days,
              escalation_days: config.escalation_days,
              notes: config.notes,
            })
            .eq('id', config.id);

          if (error) throw error;
        } else {
          // Create new
          const { error } = await db
            .from('company_sla_thresholds')
            .insert([{
              company_id: company.id,
              entity_type: 'case_risk_level',
              risk_level: riskLevel,
              internal_sla_days: config.internal_sla_days,
              regulatory_deadline_days: config.regulatory_deadline_days,
              escalation_days: config.escalation_days,
              notes: config.notes,
            }]);

          if (error) throw error;
        }
      }

      showToast('SLA configurations saved successfully', 'success');
      setEdited(false);
      await loadSLAConfigs();
      if (onSLAUpdate) onSLAUpdate();
    } catch (err) {
      console.error('Error saving SLA configs:', err);
      showToast('Error saving SLA configurations', 'error');
    } finally {
      setSaving(false);
    }
  }

  const updateConfig = (riskLevel, field, value) => {
    setSlaConfigs(prev => ({
      ...prev,
      [riskLevel]: {
        ...prev[riskLevel],
        [field]: parseInt(value) || 0,
      }
    }));
    setEdited(true);
  };

  async function resetToDefaults() {
    if (!company?.id) return;
    if (!window.confirm('Reset all SLA configurations to defaults? This will remove any custom settings.')) return;

    try {
      setResetting(true);
      // Delete all custom SLA thresholds for this company
      const { error } = await db
        .from('company_sla_thresholds')
        .delete()
        .eq('company_id', company.id)
        .eq('entity_type', 'case_risk_level');

      if (error) throw error;

      // Reset UI to defaults
      setSlaConfigs(DEFAULT_SLA_CONFIGS);
      setEdited(false);
      showToast('SLA configurations reset to defaults', 'success');
      if (onSLAUpdate) onSLAUpdate();
    } catch (err) {
      console.error('Error resetting SLA configs:', err);
      showToast('Error resetting SLA configurations', 'error');
    } finally {
      setResetting(false);
    }
  }

  const riskColors = {
    CRITICAL: '#FF6B6B',
    HIGH: '#FFA500',
    MEDIUM: '#FFC107',
    LOW: '#4CAF50',
  };

  if (loading) {
    return <div style={{ color: C.textDim }}>Loading SLA configurations...</div>;
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ color: C.text, fontWeight: 600, marginBottom: '0.5rem' }}>
          Case Risk Level SLA Configuration
        </h3>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Configure custom SLA thresholds for different case risk levels. Internal SLAs override regulatory deadlines.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {Object.entries(slaConfigs).map(([riskLevel, config]) => (
          <div
            key={riskLevel}
            style={{
              background: C.surface,
              border: `1px solid ${riskColors[riskLevel]}44`,
              borderLeft: `4px solid ${riskColors[riskLevel]}`,
              borderRadius: 8,
              padding: '1.25rem',
            }}
          >
            <div style={{ marginBottom: '1rem' }}>
              <span style={{
                color: riskColors[riskLevel],
                fontWeight: 700,
                fontSize: '1rem',
              }}>
                {riskLevel}
              </span>
              <span style={{ color: C.textDim, fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                Risk Level
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                  Internal SLA (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={config.internal_sla_days}
                  onChange={(e) => updateConfig(riskLevel, 'internal_sla_days', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem',
                  }}
                />
                <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Stricter internal deadline
                </div>
              </div>

              <div>
                <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                  Regulatory Deadline (days)
                </label>
                <input
                  type="number"
                  min="0"
                  max="365"
                  value={config.regulatory_deadline_days}
                  onChange={(e) => updateConfig(riskLevel, 'regulatory_deadline_days', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem',
                  }}
                />
                <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Regulatory minimum
                </div>
              </div>

              <div>
                <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                  Escalation Days
                </label>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={config.escalation_days || 0}
                  onChange={(e) => updateConfig(riskLevel, 'escalation_days', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: '0.9rem',
                  }}
                />
                <div style={{ color: C.textDim, fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Days before due to escalate
                </div>
              </div>
            </div>

            <div>
              <label style={{ color: C.textDim, fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                Notes
              </label>
              <textarea
                value={config.notes || ''}
                onChange={(e) => {
                  setSlaConfigs(prev => ({
                    ...prev,
                    [riskLevel]: { ...prev[riskLevel], notes: e.target.value }
                  }));
                  setEdited(true);
                }}
                placeholder="Add any notes about this SLA configuration..."
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem',
                  minHeight: '60px',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={saveSLAConfigs}
          disabled={!edited || saving}
          style={{
            padding: '0.6rem 1.2rem',
            background: edited ? C.accent : C.textDim,
            color: '#0A0E1A',
            border: 'none',
            borderRadius: 6,
            fontWeight: 700,
            cursor: edited && !saving ? 'pointer' : 'not-allowed',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem',
            opacity: edited ? 1 : 0.5,
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {edited && (
          <button
            onClick={loadSLAConfigs}
            style={{
              padding: '0.6rem 1.2rem',
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.textMid,
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: "'DM Mono', monospace",
              fontSize: '0.85rem',
            }}
          >
            Cancel
          </button>
        )}
        <button
          onClick={resetToDefaults}
          disabled={resetting}
          style={{
            padding: '0.6rem 1.2rem',
            background: 'transparent',
            border: `1px solid ${C.warn}60`,
            color: C.warn,
            borderRadius: 6,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem',
            marginLeft: 'auto',
          }}
        >
          {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
      </div>
    </div>
  );
}
